import * as THREE from 'three'
import type { FeatureResultMesh } from '../../mesh/feature'
import type {
  ImportedPartBodyBrepBinding,
  ImportedPartBodyTopologyAsset,
  ImportedTopologyFaceTriangleRangeBinding,
} from '../loaders/types'

/**
 * 导入显示 Mesh 与权威 Body 拓扑之间的运行时绑定。
 *
 * 通常一个 glTF primitive 由一个真实 `THREE.Mesh` 承载；历史合并几何也可能让同一 Mesh
 * 承载多个 primitive。本类把该 Mesh/Body 子集的局部三角形范围、primitive 集合和共享 Body
 * 拓扑收口为单一对象，避免调用方脱离 Mesh 解释局部范围。
 */
export class ImportedMeshTopologyBinding {
  /** 当前 primitive 集合对应的真实显示 Mesh。 */
  public readonly visibleMesh: THREE.Mesh

  /** 当前 Mesh 所属的 Body 绑定。 */
  private currentBodyBinding: ImportedPartBodyBrepBinding

  /** 当前 Body 的完整权威拓扑资产。 */
  private currentTopologyAsset: ImportedPartBodyTopologyAsset

  /** 当前 Mesh/Body 子集包含的 glTF primitive 下标。 */
  public readonly primitiveIndices: readonly number[]

  /** 当前 Mesh 内 face identity 到局部三角形范围的索引。 */
  private readonly faceTriangleRangeByIdentity = new Map<string, [number, number][]>()

  /** 按局部三角形起点排序的反向索引；hover 二分查询，不逐面扫描完整 Body。 */
  private readonly topologyFacesByTriangleRange: ImportedMeshTopologyFaceTriangleInterval[] = []

  /** 当前绑定按需创建的 pick host。 */
  private pickHost: FeatureResultMesh | null = null

  /**
   * 创建导入 Mesh 拓扑绑定。
   *
   * @param input 绑定构造输入。
   * @throws primitive 身份、Body 身份或局部三角形范围不满足绑定契约时抛出错误。
   */
  public constructor(input: ImportedMeshTopologyBindingInput) {
    this.visibleMesh = input.mesh
    this.currentBodyBinding = input.bodyBinding
    this.currentTopologyAsset = input.topologyAsset
    this.primitiveIndices = Object.freeze([...input.primitiveIndices])

    this.assertIdentityConsistency()
    this.indexFaceTriangleRanges()
  }

  /**
   * 返回当前运行时 Body 绑定 ID。
   *
   * @returns Body 绑定 ID。
   */
  public get bodyBindingId(): string {
    return this.bodyBinding.bodyBindingId
  }

  /**
   * 读取单 primitive Mesh 的 primitive 下标。
   *
   * 多 group Mesh 不存在唯一 primitive，本属性返回 `null`，调用方应使用 `primitiveIndices`。
   *
   * @returns 唯一 primitive 下标；当前绑定承载多个 primitive 时返回 `null`。
   */
  public get primitiveIndex(): number | null {
    return this.primitiveIndices.length === 1 ? this.primitiveIndices[0] ?? null : null
  }

  /**
   * 读取当前 Mesh 的 Body 绑定。
   *
   * @returns 当前 Body 绑定。
   */
  public get bodyBinding(): ImportedPartBodyBrepBinding {
    return this.currentBodyBinding
  }

  /**
   * 读取当前 Mesh 共享的 Body 拓扑资产。
   *
   * @returns 当前 Body 拓扑资产。
   */
  public get topologyAsset(): ImportedPartBodyTopologyAsset {
    return this.currentTopologyAsset
  }

  /**
   * 在项目 occurrence 最终化时原子更新运行时 Body 身份。
   *
   * @param nextBodyBindingId 最终 Body 绑定 ID。
   * @throws 目标 Body 绑定 ID 为空时抛出错误。
   */
  public rebindBodyBindingId(nextBodyBindingId: string): void {
    const normalizedBodyBindingId = nextBodyBindingId.trim()
    if (!normalizedBodyBindingId) {
      throw new Error('导入 Mesh 拓扑绑定不能重绑定到空 Body ID')
    }
    this.currentBodyBinding = {
      ...this.currentBodyBinding,
      bodyBindingId: normalizedBodyBindingId,
    }
    this.currentTopologyAsset = {
      ...this.currentTopologyAsset,
      bodyBindingId: normalizedBodyBindingId,
      meshData: {
        ...this.currentTopologyAsset.meshData,
        metadata: {
          ...(this.currentTopologyAsset.meshData.metadata ?? {}),
          bodyBindingId: normalizedBodyBindingId,
        },
      },
    }
  }

  /**
   * 按拓扑面身份解析当前 Mesh 内的局部三角形范围。
   *
   * @param face 目标拓扑面身份。
   * @returns 当前 Mesh 内的局部半开区间；未命中时返回 `null`。
   */
  public resolveFaceTriangleRange(
    face: ImportedMeshTopologyFaceIdentity,
  ): [number, number] | null {
    const ranges = this.resolveFaceTriangleRanges(face).sort((left, right) => left[0] - right[0])
    const first = ranges[0]
    if (!first) return null
    const merged: [number, number] = [first[0], first[1]]
    for (const range of ranges.slice(1)) {
      // 单区间调用方不能表达孔洞，必须让其退回完整几何，而不是覆盖间隙中的其他面。
      if (range[0] !== merged[1]) return null
      merged[1] = range[1]
    }
    return merged
  }

  /**
   * 读取当前 Mesh 中某个拓扑面的全部显式片段，保持各片段的精确边界。
   * @param face 完整 Body 中的面身份。
   * @returns 独立的局部半开区间副本；缺少绑定时返回空数组。
   */
  public resolveFaceTriangleRanges(face: ImportedMeshTopologyFaceIdentity): [number, number][] {
    for (const key of this.createFaceIdentityKeys(face)) {
      const ranges = this.faceTriangleRangeByIdentity.get(key)
      if (ranges) {
        return ranges.map(range => [range[0], range[1]])
      }
    }
    return []
  }

  /**
   * 将显示 Mesh 的局部三角形编号解析为其显式绑定的权威 Body 面。
   *
   * GLB 和 BRep host 的三角形顺序可以不同，二次射线也可能先命中另一个重合面。
   * 此处只消费加载时确认的范围契约，不将显示三角形编号直接当作 BRep 面编号。
   * 返回原始快照引用，供调用方校验它与共享 pick host 是否属于同一拓扑快照。
   *
   * @param triangleIndex 第一阶段可见 Mesh 命中的局部三角形编号。
   * @returns 显式绑定的面；非法编号、范围空洞或缺少面身份时返回 null。
   */
  public resolveTopologyFaceByTriangleIndex(triangleIndex: number): TopologySnapshotFace | null {
    if (!Number.isInteger(triangleIndex) || triangleIndex < 0) {
      return null
    }
    let lower = 0
    let upper = this.topologyFacesByTriangleRange.length - 1
    while (lower <= upper) {
      const middle = Math.floor((lower + upper) / 2)
      const interval = this.topologyFacesByTriangleRange[middle]!
      if (triangleIndex < interval.triangleRange[0]) {
        upper = middle - 1
      } else if (triangleIndex >= interval.triangleRange[1]) {
        lower = middle + 1
      } else {
        return interval.face
      }
    }
    return null
  }

  /**
   * 读取当前绑定持有的 pick host。
   *
   * @returns pick host；尚未创建时返回 `null`。
   */
  public getPickHost(): FeatureResultMesh | null {
    return this.pickHost
  }

  /**
   * 记录当前绑定按需创建的 pick host。
   *
   * @param host 当前 Body 的 pick host。
   * @throws 当前绑定已经持有另一个 pick host 时抛出错误。
   */
  public attachPickHost(host: FeatureResultMesh): void {
    if (this.pickHost && this.pickHost !== host) {
      throw new Error(`导入 Mesh 拓扑绑定已经持有不同 pick host: ${this.bodyBindingId}`)
    }
    this.pickHost = host
  }

  /**
   * 解除并返回当前 pick host，由 Repository 负责后续资源释放。
   *
   * @returns 原 pick host；不存在时返回 `null`。
   */
  public detachPickHost(): FeatureResultMesh | null {
    const host = this.pickHost
    this.pickHost = null
    return host
  }

  /**
   * 校验 primitive、Body binding 与拓扑资产身份一致性。
   */
  private assertIdentityConsistency(): void {
    if (
      this.primitiveIndices.length === 0
      || this.primitiveIndices.some((primitiveIndex) => (
        !Number.isInteger(primitiveIndex) || primitiveIndex < 0
      ))
    ) {
      throw new Error(`导入 Mesh primitiveIndices 无效: ${this.primitiveIndices.join(',')}`)
    }
    if (this.bodyBindingId !== this.topologyAsset.bodyBindingId) {
      throw new Error(
        `导入 Mesh Body 拓扑身份不一致: binding=${this.bodyBindingId}, asset=${this.topologyAsset.bodyBindingId}`,
      )
    }
  }

  /**
   * 一次性建立当前 Mesh 的面身份与局部三角形区间双向索引。
   */
  private indexFaceTriangleRanges(): void {
    const triangleCount = this.resolveVisibleMeshTriangleCount()
    const rangeBindings = this.bodyBinding.topologyFaceTriangleRanges ?? []
    if (rangeBindings.length === 0) {
      return
    }
    const topologyFaceByIdentity = new Map<string, TopologySnapshotFace>()
    for (const face of this.topologyAsset.meshData.topologySnapshot?.faces ?? []) {
      for (const key of this.createFaceIdentityKeys(face)) {
        if (topologyFaceByIdentity.has(key)) {
          throw new Error(`导入 Body 拓扑面身份重复: body=${this.bodyBindingId}, identity=${key}`)
        }
        topologyFaceByIdentity.set(key, face)
      }
    }

    for (const binding of rangeBindings) {
      const range = this.cloneAndValidateTriangleRange(binding, triangleCount)
      let topologyFace: TopologySnapshotFace | null = null
      /** 同一 face 同时登记多种稳定身份，调用方无需了解当前 sidecar 使用哪套引用。 */
      for (const key of this.createBindingIdentityKeys(binding)) {
        const ranges = this.faceTriangleRangeByIdentity.get(key) ?? []
        ranges.push([range[0], range[1]])
        this.faceTriangleRangeByIdentity.set(key, ranges)
        const matchedFace = topologyFaceByIdentity.get(key)
        if (matchedFace && topologyFace && matchedFace !== topologyFace) {
          throw new Error(
            `导入 Mesh 拓扑面范围身份冲突: mesh=${this.visibleMesh.uuid}, body=${this.bodyBindingId}, identity=${key}`,
          )
        }
        topologyFace = matchedFace ?? topologyFace
      }
      this.topologyFacesByTriangleRange.push({ triangleRange: range, face: topologyFace })
    }

    /** 排序后只需比较相邻区间；避免为密集 Body 建索引时进行平方级重叠检查。 */
    this.topologyFacesByTriangleRange.sort((left, right) => left.triangleRange[0] - right.triangleRange[0])
    for (let index = 1; index < this.topologyFacesByTriangleRange.length; index += 1) {
      const previous = this.topologyFacesByTriangleRange[index - 1]!
      const current = this.topologyFacesByTriangleRange[index]!
      if (current.triangleRange[0] < previous.triangleRange[1]) {
        throw new Error(
          `导入 Mesh 拓扑面范围重叠: mesh=${this.visibleMesh.uuid}, body=${this.bodyBindingId}, range=${current.triangleRange.join(':')}`,
        )
      }
    }
  }

  /**
   * 读取当前 Mesh 的三角形总数。
   *
   * @returns 三角形数量。
   */
  private resolveVisibleMeshTriangleCount(): number {
    const index = this.visibleMesh.geometry.getIndex()
    if (index) {
      return Math.floor(index.count / 3)
    }
    const position = this.visibleMesh.geometry.getAttribute('position')
    return position ? Math.floor(position.count / 3) : 0
  }

  /**
   * 复制并校验单个局部三角形范围。
   *
   * @param binding 原始 face range 绑定。
   * @param triangleCount 当前 Mesh 的三角形总数。
   * @returns 校验后的范围副本。
   * @throws 范围不是合法半开区间或超出当前 Mesh 三角形数量时抛出错误。
   */
  private cloneAndValidateTriangleRange(
    binding: ImportedTopologyFaceTriangleRangeBinding,
    triangleCount: number,
  ): [number, number] {
    const [start, end] = binding.triangleRange
    if (
      !Number.isInteger(start)
      || !Number.isInteger(end)
      || start < 0
      || end <= start
      || end > triangleCount
    ) {
      throw new Error(
        `导入 Mesh 拓扑面范围越界: mesh=${this.visibleMesh.uuid}, body=${this.bodyBindingId}, range=${start}:${end}, triangles=${triangleCount}`,
      )
    }
    return [start, end]
  }

  /**
   * 创建拓扑面查询身份键。
   *
   * @param face 目标面身份。
   * @returns 按稳定性排序的候选键。
   */
  private createFaceIdentityKeys(face: ImportedMeshTopologyFaceIdentity): string[] {
    return this.createIdentityKeys(face.ref, face.subEntityId, face.index)
  }

  /**
   * 创建 face range 绑定身份键。
   *
   * @param binding face range 绑定。
   * @returns 可注册的身份键。
   */
  private createBindingIdentityKeys(binding: ImportedTopologyFaceTriangleRangeBinding): string[] {
    return this.createIdentityKeys(binding.faceRef, binding.subEntityId, binding.faceIndex)
  }

  /**
   * 统一创建面持久引用、子实体 ID 和索引键。
   *
   * @param faceRef 面持久引用。
   * @param subEntityId 面子实体 ID。
   * @param faceIndex 面索引。
   * @returns 非空身份键。
   */
  private createIdentityKeys(
    faceRef: string | null | undefined,
    subEntityId: string | null | undefined,
    faceIndex: number | undefined,
  ): string[] {
    const keys: string[] = []
    const normalizedFaceRef = faceRef?.trim()
    const normalizedSubEntityId = subEntityId?.trim()
    if (normalizedFaceRef) {
      keys.push(`ref:${normalizedFaceRef}`)
    }
    if (normalizedSubEntityId) {
      keys.push(`subEntityId:${normalizedSubEntityId}`)
    }
    if (Number.isInteger(faceIndex)) {
      keys.push(`index:${String(faceIndex)}`)
    }
    return keys
  }
}
